// src/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { api_noIntercept } from '../api/axios';
import {
  getHeaderAccessToken,
  getHeaderRefreshToken,
  applyHeaderTokensFromResponse,
  clearHeaderTokens,
} from '../api/headerTokens';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export function isAutoLoginMode(user) {
  if (user?.autoLogin) return true;
  return sessionStorage.getItem('ia_authMode') === 'autologin';
}

export function setAutoLoginFlag() {
  sessionStorage.setItem('ia_authMode', 'autologin');
}

export function clearAutoLoginFlag() {
  sessionStorage.removeItem('ia_authMode');
}

export function setAutoLoginErrorMessage(msg) {
  sessionStorage.setItem('ia_autologin_error', msg ?? 'Onbekende fout tijdens automatische login.');
}

export function consumeAutoLoginErrorMessage() {
  const m = sessionStorage.getItem('ia_autologin_error');
  sessionStorage.removeItem('ia_autologin_error');
  return m;
}

const AUTOLOGIN_RECOVERY_KEY = 'ia_autologin_recovery_at';
const AUTOLOGIN_RECOVERY_COOLDOWN_MS = 60_000;

function isExternalTokenRoute(pathname = window.location.pathname, search = window.location.search) {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  const params = new URLSearchParams(search);

  return (
    ['/auto-login', '/qr-login', '/form', '/form-qr'].includes(normalizedPath) &&
    params.has('token')
  );
}

function isStandaloneTokenRoute() {
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
  return ['/form', '/form-qr'].includes(normalizedPath) && isExternalTokenRoute();
}

function setRequestAuthorization(config, token) {
  config.headers = config.headers || {};

  if (typeof config.headers.set === 'function') {
    config.headers.set('Authorization', `Bearer ${token}`);
  } else {
    config.headers.Authorization = `Bearer ${token}`;
  }
}

/** =========================
 *  BRANDING helpers
 *  ========================= */
const isValidHex = (v) => /^#([0-9A-F]{3}|[0-9A-F]{6})$/i.test((v || '').trim());
const normalizeHex = (v, fallback) => (isValidHex(v) ? v.trim() : fallback);

const darkenHex = (hex, amount = 0.18) => {
  const h = normalizeHex(hex, null);
  if (!h) return hex;

  const raw = h.length === 4 ? `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}` : h;

  const r = parseInt(raw.slice(1, 3), 16);
  const g = parseInt(raw.slice(3, 5), 16);
  const b = parseInt(raw.slice(5, 7), 16);

  const d = (c) => Math.max(0, Math.min(255, Math.round(c * (1 - amount))));
  const toHex = (n) => n.toString(16).padStart(2, '0');

  return `#${toHex(d(r))}${toHex(d(g))}${toHex(d(b))}`;
};

const extractBranding = (u) => {
  // accepteer beide vormen:
  // 1) u.branding = { primary, secondary, ...}
  // 2) u.branding = { branding: { primary, secondary }, portal_branding: {...} }
  const b = u?.branding;
  if (!b) return null;

  const primary = b?.primary ?? b?.branding?.primary;
  const secondary = b?.secondary ?? b?.branding?.secondary;

  return {
    primary: normalizeHex(primary, '#FFFFFF'),
    secondary: normalizeHex(secondary, '#373187'),
  };
};

const applyCompanyBrandingVars = (u) => {
  const branding = extractBranding(u);
  if (!branding) return;

  // ✅ consistent scoping: alles op BODY (zoals je later in Main/Customer Portal hebt gedaan)
  const scope = document.body;

  scope.style.setProperty('--header-bg', branding.primary);
  scope.style.setProperty('--header-line-border-color', branding.primary);

  scope.style.setProperty('--button-bg', branding.secondary);
  scope.style.setProperty('--button-hover-bg', darkenHex(branding.secondary, 0.18));
  scope.style.setProperty('--button-color', '#ffffff');
};

const clearCompanyBrandingVars = () => {
  const scope = document.body;

  [
    '--header-bg',
    '--header-line-border-color',
    '--header-line-border-width',
    '--button-bg',
    '--button-hover-bg',
    '--button-color',
    '--menu-bg',
    '--card-bg',
    '--detail-page-bg',
    '--body-bg',
    '--cp-header-bg',
    '--cp-header-line',
  ].forEach((k) => scope.style.removeProperty(k));

  document.body.classList.remove('customer-portal');
};

export const applyBrandingFromObject = (brandingObj) => {
  const u = { branding: brandingObj };
  applyCompanyBrandingVars(u);
};

export const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const redirectingRef = useRef(false);
  const refreshPromiseRef = useRef(null);
  const navigate = useNavigate();

  const updateUser = (updatedFields) => {
    setUser((prev) => {
      const merged = { ...(prev || {}), ...updatedFields };
      // ✅ als iemand branding update via updateUser, meteen toepassen
      applyCompanyBrandingVars(merged);
      return merged;
    });
  };

  // 1) Bootstrap sessie
  useEffect(() => {
    // Een externe tokenroute moet eerst zelf /auto-login uitvoeren.
    // Een oude Afterbase-cookie of header-token mag dit niet onderscheppen.
    if (isExternalTokenRoute()) {
      setLoading(false);
      return;
    }

    api
      .get('/auth/me')
      .then((res) => {
        const u = res.data.user;
        setUser(u);
        setCompanyId(u.default_companyid);
        setCompanyName(u.default_companyname);

        if (u.default_companyid) {
          sessionStorage.setItem('ia_last_company_id', String(u.default_companyid));
        }

        // ✅ BRANDING direct toepassen na bootstrap
        applyCompanyBrandingVars(u);

        try {
          if (u?.branding) {
            localStorage.setItem('iA-branding', JSON.stringify(u.branding));
            window.dispatchEvent(new Event('ia-branding-updated'));
          }
        } catch {}
      })
      .catch(() => {
        setUser(null);
        setCompanyId(null);
        setCompanyName(null);

        // ✅ reset vars naar defaults
        clearCompanyBrandingVars();
      })
      .finally(() => setLoading(false));
  }, []);

  const setUserFromMe = async () => {
    const res = await api.get('/auth/me');
    const u = res.data.user;

    setUser(u);
    setCompanyId(u.default_companyid);
    setCompanyName(u.default_companyname);

    if (u.default_companyid) {
      sessionStorage.setItem('ia_last_company_id', String(u.default_companyid));
    }

    // ✅ BRANDING toepassen
    applyCompanyBrandingVars(u);

    try {
      if (u?.branding) localStorage.setItem('iA-branding', JSON.stringify(u.branding));
    } catch {}

    return u;
  };

  // 2) Axios response-interceptor: 401 → refresh → retry; bij falen: popup + redirect (3s)
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (resp) => resp,
      async (err) => {
        const orig = err.config || {};
        const url = orig.url || '';

        // Skip bepaalde endpoints om loops te voorkomen
        if (orig._retry || url.endsWith('/login') || url.endsWith('/verify_2fa') || url.endsWith('/auth/refresh') || url.endsWith('/auth/logout')) {
          return Promise.reject(err);
        }

        if (err.response?.status === 401) {
          orig._retry = true;
          try {
            // Laat gelijktijdige 401-responses dezelfde refresh delen.
            if (!refreshPromiseRef.current) {
              refreshPromiseRef.current = (async () => {
                const headerRefreshToken = getHeaderRefreshToken();

                const response = headerRefreshToken
                  ? await api_noIntercept.post('/auth/refresh', null, {
                      headers: {
                        Authorization: `Bearer ${headerRefreshToken}`,
                      },
                    })
                  : await api_noIntercept.post('/auth/refresh');

                applyHeaderTokensFromResponse(response.data);
                return response;
              })().finally(() => {
                refreshPromiseRef.current = null;
              });
            }

            await refreshPromiseRef.current;

            // De originele Axios-config kan nog het verlopen access-token bevatten.
            // Vervang dat expliciet voordat de aanvraag opnieuw wordt uitgevoerd.
            const newAccessToken = getHeaderAccessToken();
            if (newAccessToken) {
              setRequestAuthorization(orig, newAccessToken);
            }

            return api(orig);
          } catch (refreshErr) {
            if (!redirectingRef.current) {
              redirectingRef.current = true;

              const targetCompanyId = companyId || user?.default_companyid;

              clearHeaderTokens();
              setUser(null);
              setCompanyId(null);
              setCompanyName(null);

              // ✅ reset vars naar defaults
              clearCompanyBrandingVars();

              if (isStandaloneTokenRoute()) {
                const previousRecovery = Number(
                  sessionStorage.getItem(AUTOLOGIN_RECOVERY_KEY) || 0
                );
                const mayRetry =
                  !previousRecovery ||
                  Date.now() - previousRecovery > AUTOLOGIN_RECOVERY_COOLDOWN_MS;

                if (mayRetry) {
                  // De IXON-URL blijft behouden. Na de reload voert StandaloneForm
                  // opnieuw /auto-login uit en ontvangt een volledig verse sessie.
                  sessionStorage.setItem(AUTOLOGIN_RECOVERY_KEY, String(Date.now()));
                  window.location.reload();
                } else {
                  setAutoLoginErrorMessage(
                    'The IXON session could not be renewed automatically. Reopen the page from IXON Cloud.'
                  );
                  navigate('/integration-error', { replace: true });
                }
              } else if (isAutoLoginMode(user)) {
                setAutoLoginErrorMessage(
                  'Session expired. Reopen this page from the external application.'
                );
                navigate('/integration-error', { replace: true });
              } else {
                setError('Session expired. Redirecting to login page.');
                setShowErrorPopup(true);

                navigate(targetCompanyId ? `/login/${targetCompanyId}` : '/login', {
                  replace: true
                });

                setTimeout(() => {
                  setShowErrorPopup(false);
                }, 3000);
              }
            }
            return Promise.reject(refreshErr);
          }
        }

        return Promise.reject(err);
      }
    );

    return () => api.interceptors.response.eject(interceptor);
  }, [companyId, user, navigate]);

  // 3) Klassieke login
  // ✅ BEST PRACTICE: login-response bevat branding → direct toepassen (geen extra /auth/me nodig)
  const login = async (email, password, remember) => {
    redirectingRef.current = false;

    // Voorkom dat een geldige header-token uit een eerdere IXON-sessie na de
    // klassieke login voorrang krijgt op de nieuwe login-cookie.
    clearHeaderTokens();
    clearAutoLoginFlag();

    const res = await api.post('/login', {
      email,
      password,
      keepMeSignedIn: remember
    });

    // Two-step login: with MFA no session exists yet; the caller asks for the one-time password
    // and calls completeMfaLogin with the challenge.
    if (res.data.mfa_required) {
      return { mfa_required: true, mfa_token: res.data.mfa_token, mfa_enabled: true };
    }

    return applyLoggedInUser(res.data.user || {});
  };

  // 3b) Second step of the login with MFA: the session cookies are only set by /verify_2fa.
  const completeMfaLogin = async (mfaToken, otp) => {
    const res = await api.post('/verify_2fa', { mfa_token: mfaToken, otp });
    return applyLoggedInUser(res.data.user || {});
  };

  const applyLoggedInUser = (u) => {
    setUser(u);
    setCompanyId(u.default_companyid);
    setCompanyName(u.default_companyname);

    if (u.default_companyid) {
      sessionStorage.setItem('ia_last_company_id', String(u.default_companyid));
    }

    // ✅ direct apply
    applyCompanyBrandingVars(u);

    return u;
  };

  // 4) Company switch
  const switchCompany = (id, name) => {
    setCompanyId(id);
    setCompanyName(name);
  };

  // 5) Logout
  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (_) {
      // negeer netwerkfout bij logout
    } finally {
      clearHeaderTokens();
      setUser(null);
      setCompanyId(null);
      setCompanyName(null);

      // ✅ reset vars naar defaults
      clearCompanyBrandingVars();

      clearAutoLoginFlag();
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <AuthContext.Provider
      value={{
        user,
        companyId,
        companyName,
        login,
        completeMfaLogin,
        updateUser,
        switchCompany,
        logout,
        setUserFromMe,
        loading,
      }}
    >
      {showErrorPopup && <div className="popup">{error}</div>}
      {children}
    </AuthContext.Provider>
  );
};
